import { useI18N } from "common/hooks";
import { getVenueDataSource, hasVenueData } from "common/utils";
import { formatDateShort } from "common/utils/general";

type VenueDataSourceInput = {
  // Venue uses the plural name, VenueDetails the singular — accept either.
  reviewsDetailsSubmitted?: number;
  reviewDetailsSubmitted?: number;
  hasExperiences?: boolean;
  hasAlerts?: boolean;
  hasTeamTags?: boolean;
  hasGeneralUserPhotos?: boolean;
  latestReviewDateUpdated?: string;
};

export function VenueDataSourceBadge({
  venue,
  className = "flex items-center gap-1",
  iconClassName = "w-5 h-5 shrink-0",
  labelClassName = "label-m-semibold text-fg-neutral-subtle whitespace-nowrap",
}: {
  venue: VenueDataSourceInput;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
}) {
  const i18n = useI18N();
  const signals = {
    reviewsDetailsSubmitted:
      venue.reviewsDetailsSubmitted ?? venue.reviewDetailsSubmitted ?? 0,
    hasExperiences: venue.hasExperiences,
    hasAlerts: venue.hasAlerts,
    hasTeamTags: venue.hasTeamTags,
    hasGeneralUserPhotos: venue.hasGeneralUserPhotos,
  };
  const dataSource = getVenueDataSource(signals);
  if (!hasVenueData(signals) || dataSource === "none") return null;

  // Same team/user badge as VenueCard's "Variant indicators" — replaces the
  // old businessVerified text everywhere on the venue profile page.
  const BADGE = {
    team: {
      icon: "/images/sociability_verified.svg",
      label: i18n.get("sociabilityVerified"),
    },
    user: {
      icon: "/images/user_updated.svg",
      label: i18n.get("userUpdated"),
    },
  } as const;

  const lastUpdated = formatDateShort(venue.latestReviewDateUpdated || "");

  return (
    <div className={className}>
      <img
        src={BADGE[dataSource].icon}
        alt=""
        aria-hidden="true"
        className={iconClassName}
      />
      <span className={labelClassName}>
        {BADGE[dataSource].label}
        {lastUpdated ? `: ${lastUpdated}` : ""}
      </span>
    </div>
  );
}
